import { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import {
    exportFlowsForTranslation,
    getAllCultures,
    getFlowLabels,
    getFlows,
    getTranslationLanguagesForFlows,
} from '../../sources/flow';
import translations from '../../translations';
import type { FlowResponseAPI } from '../../types/flow';
import type { Culture, TranslateFlowExportRequest } from '../../types/translation';
import { getSharedStyles } from '../../utils/select';
import { stringReplace } from '../../utils/string';
import ButtonDefault from '../buttons/ButtonDefault';
import ButtonPrimary from '../buttons/ButtonPrimary';
import FormGroup from '../generic/FormGroup';
import GenericModal from '../generic/modal/GenericModal';
import Loader from '../loader/Loader';
import { useNotificationDispatch } from '../notifications/NotificationProvider';

enum ExportType {
    ByFlow,
    ByLabel,
}

interface Props {
    container: HTMLElement | null | undefined;
    setIsProcessing: (isProcessing: boolean) => void;
    isActive: boolean;
}

const TranslationExport = ({ container, setIsProcessing, isActive }: Props) => {
    const [flows, setFlows] = useState<FlowResponseAPI[]>([]);
    const [flowLabels, setFlowLabels] = useState<string[]>([]);
    const [selectedFlows, setSelectedFlows] = useState<string[]>([]);
    const [cultures, setCultures] = useState<Culture[]>([]);
    const [sortedCultures, setSortedCultures] = useState<Culture[]>([]); // Cultures but with existing usages for the selected Flows sorted to the top.
    const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
    const [exportType, setExportType] = useState(ExportType.ByFlow);
    const [excludeCompletedTranslations, setExcludeCompletedTranslations] =
        useState<boolean>(false);
    const [alreadyCompletedFlows, setAlreadyCompletedFlows] = useState<number>(0);

    // Loading
    const [isLoadingCultures, setIsLoadingCultures] = useState(true);
    const [isLoadingFlows, setIsLoadingFlows] = useState(true);

    const dispatchNotification = useNotificationDispatch();

    const notifyError = useCallback(
        (error: unknown) => dispatchNotification({ type: 'notifyError', payload: { error } }),
        [dispatchNotification],
    );

    useEffect(() => {
        setIsLoadingCultures(true);

        getAllCultures()
            .then((response: Culture[]) => {
                setCultures(response);
                setSortedCultures(response);
            })
            .catch(notifyError)
            .finally(() => {
                setIsLoadingCultures(false);
            });
    }, [notifyError]);

    useEffect(() => {
        // Reload the Flows and Labels when the tab is re-visited in case there have been changes, but don't do the same to the cultures which are mostly static.
        if (isActive) {
            setIsLoadingFlows(true);
            setSelectedFlows([]);
            setExcludeCompletedTranslations(false);

            getFlows()
                .then((response: FlowResponseAPI[]) => setFlows(response))
                .catch(notifyError)
                .finally(() => {
                    setIsLoadingFlows(false);
                });

            getFlowLabels()
                .then((labels) => setFlowLabels(labels))
                .catch(notifyError);
        }
    }, [isActive, notifyError]);

    // To make it easier to re-export existing translations we can move the used languages to the top of the dropdown.
    const moveUsedLanguagesToTopOfSelect = (flowIds: string[]) => {
        getTranslationLanguagesForFlows({ flowIds })
            .then((response) => {
                // Filter out the default culture because this isn't relevant to the target language.
                const filteredResponse: Culture[] = response
                    .filter(
                        (r) => r.language !== 'EN' && r.developerSummary !== 'Created by system', // We have to use a combination of language and developerSummary as the ID is unique for everyone.
                    )
                    .map((fr) => ({
                        code: fr.code,
                        name: fr.developerName,
                    }));

                // Use the selected Flows that already have translations to filter the list of all languages.
                const unusedCultures = cultures.filter((c) =>
                    filteredResponse.every((fr) => fr.code !== c.code),
                );

                // Add the existing languages to the top of the list so they appear first in the dropdown.
                setSortedCultures([...filteredResponse, ...unusedCultures]);
            })
            .catch(notifyError);
    };

    const getAllFlowOptions = () => {
        return flows.map((flow) => ({ label: flow.developerName, value: flow.id.id }));
    };

    const getAllLabelOptions = () => {
        return flowLabels.map((label) => ({ label, value: label }));
    };

    const getAllLanguageOptions = () =>
        sortedCultures.map((culture) => ({ label: culture.name, value: culture.code }));

    const exportFlow = async () => {
        try {
            if (selectedFlows && selectedFlows.length > 0 && targetLanguages.length > 0) {
                setIsProcessing(true);

                const translateFlowExportRequest: TranslateFlowExportRequest = {
                    flowIds: selectedFlows,
                    targetLanguages,
                    excludeCompletedTranslations,
                };
                const { completedFlows } = await exportFlowsForTranslation({
                    translateFlowExportRequest,
                });

                setAlreadyCompletedFlows(completedFlows);
            }
        } catch (error) {
            notifyError(error);
        } finally {
            setIsProcessing(false);
        }
    };

    const exportTypeContent = (
        <div className="export-selector-wrapper">
            <FormGroup
                label={translations.TRANSLATIONS_export_type_select_label}
                htmlFor="translation-export-type-select"
            >
                <select
                    id="translation-export-type-select"
                    className="form-control select-flow-export-type"
                    value={exportType}
                    onChange={({ target: { value } }) => {
                        setSelectedFlows([]);
                        setExportType(Number(value));
                    }}
                >
                    <option value={ExportType.ByFlow}>
                        {translations.TRANSLATIONS_export_by_flow_label}
                    </option>
                    <option value={ExportType.ByLabel}>
                        {translations.TRANSLATIONS_export_by_label_label}
                    </option>
                </select>
            </FormGroup>

            {exportType === ExportType.ByFlow && (
                <FormGroup
                    label={translations.TRANSLATIONS_flow_label}
                    htmlFor="translation-flow-select"
                    isRequired
                >
                    <Select
                        inputId="translation-flow-select"
                        className="select-flow"
                        styles={getSharedStyles<
                            ReturnType<typeof getAllFlowOptions>[number],
                            true
                        >()}
                        options={getAllFlowOptions()}
                        onChange={(selectedOptions) => {
                            const newSelectedFlows = selectedOptions.map((option) => option.value);

                            // if there are less flows after then it means one or more has been cleared so get the relevant languages again.
                            if (newSelectedFlows.length < selectedFlows.length) {
                                moveUsedLanguagesToTopOfSelect(newSelectedFlows);
                            }

                            setSelectedFlows(newSelectedFlows);
                        }}
                        onBlur={() => {
                            if (selectedFlows.length > 0) {
                                moveUsedLanguagesToTopOfSelect(selectedFlows);
                            }
                        }}
                        placeholder={translations.COMMON_select_flow_placeholder}
                        menuPlacement="auto"
                        isMulti
                        closeMenuOnSelect={false}
                    />
                </FormGroup>
            )}
            {exportType === ExportType.ByLabel && (
                <FormGroup
                    label={translations.TRANSLATIONS_flow_labels_label}
                    htmlFor="translation-label-select"
                    isRequired
                >
                    <Select
                        inputId="translation-label-select"
                        className="select-flow"
                        styles={getSharedStyles<
                            ReturnType<typeof getAllLabelOptions>[number],
                            true
                        >()}
                        options={getAllLabelOptions()}
                        onChange={(selectedOptions) => {
                            const selectedFlowIdsByTag = flows
                                .filter(({ tags }) =>
                                    tags.some((tag) =>
                                        selectedOptions.map(({ value: tag }) => tag).includes(tag),
                                    ),
                                )
                                .map(({ id: { id } }) => id);

                            // if there are less flows after then it means one or more has been cleared so get the relevant languages again.
                            if (selectedFlowIdsByTag.length < selectedFlows.length) {
                                moveUsedLanguagesToTopOfSelect(selectedFlowIdsByTag);
                            }

                            setSelectedFlows(selectedFlowIdsByTag);
                        }}
                        onBlur={() => {
                            if (selectedFlows.length > 0) {
                                moveUsedLanguagesToTopOfSelect(selectedFlows);
                            }
                        }}
                        placeholder={translations.TRANSLATIONS_select_a_flow_label_placeholder}
                        menuPlacement="auto"
                        isMulti
                        closeMenuOnSelect={false}
                    />
                </FormGroup>
            )}
        </div>
    );

    const languageInputs = (
        <FormGroup
            label={translations.TRANSLATIONS_target_language_label}
            htmlFor="translation-target-select"
            isRequired
        >
            <Select
                inputId="translation-target-select"
                className="language-select"
                styles={getSharedStyles<ReturnType<typeof getAllLanguageOptions>[number], true>()}
                options={getAllLanguageOptions()}
                onChange={(selectedOptions) =>
                    setTargetLanguages(selectedOptions.map((option) => option.value))
                }
                placeholder={translations.TRANSLATIONS_target_select_placeholder}
                menuPlacement="auto"
                isMulti
                closeMenuOnSelect={false}
            />
        </FormGroup>
    );

    const excludeCompletedTranslationsCheckbox = (
        <div className="exclude-completed-wrapper">
            <label htmlFor="input-exclude-completed">
                {translations.TRANSLATIONS_exclude_completed_label_text}
                <input
                    id="input-exclude-completed"
                    className="exclude-completed-checkbox"
                    type="checkbox"
                    checked={excludeCompletedTranslations}
                    onChange={(e) => setExcludeCompletedTranslations(e.target.checked)}
                />
            </label>
            <span className="help-block">
                {translations.TRANSLATIONS_exclude_completed_help_text}
            </span>
        </div>
    );

    const completedTranslationConfirmationModal = (
        <GenericModal
            title={translations.TRANSLATIONS_export_message_modal_title}
            renderBody={() => (
                <div className="translation-export-message-wrapper">
                    <div className="bold">
                        {stringReplace(
                            alreadyCompletedFlows > 1
                                ? translations.TRANSLATIONS_already_completed_message_plural
                                : translations.TRANSLATIONS_already_completed_message_singular,
                            { count: alreadyCompletedFlows },
                        )}
                    </div>
                    <div>{translations.TRANSLATIONS_remaining_downloaded_message}</div>
                </div>
            )}
            renderFooter={() => (
                <ButtonDefault onClick={() => setAlreadyCompletedFlows(0)}>
                    {translations.COMMON_ok}
                </ButtonDefault>
            )}
            show={alreadyCompletedFlows > 0}
            container={container}
            onHide={() => setAlreadyCompletedFlows(0)}
            dialogClassName="modal-dialog-large"
        />
    );

    if (isLoadingCultures || isLoadingFlows) {
        return <Loader />;
    }

    return (
        <>
            <h4>{translations.TRANSLATIONS_export_header}</h4>
            {exportTypeContent}
            {languageInputs}
            {excludeCompletedTranslationsCheckbox}
            <ButtonPrimary
                onClick={exportFlow}
                disabled={!(selectedFlows.length > 0 && targetLanguages.length > 0)}
            >
                {translations.TRANSLATIONS_export_button}
            </ButtonPrimary>

            {completedTranslationConfirmationModal}
        </>
    );
};

export default TranslationExport;
